import { useState, useEffect, useCallback, useRef } from 'react';
// Frontend-safe placeholder implementations to avoid importing server modules in UI build
export function createBeeMemoryProvider() {
  return {
    get: async (_key: string, _options?: any) => null,
    set: async (_key: string, _value: unknown, _ttl?: number, _options?: any) => void 0,
    subscribe: async (_pattern: string, _callback: any) => null,
  } as unknown as any;
}

export class BeeMemoryProvider {

  async get(_key: string, _options?: any): Promise<any> { return null; }
  async set(_key: string, _value: any, _ttl?: number, _options?: any): Promise<void> { }
  async subscribe(_pattern: string, _callback: any): Promise<any> { return null; }
}

interface GridRow {
  id: string;
  [key: string]: any;
}

interface GridColumn {
  id: string;
  header: string;
  type: 'text' | 'number' | 'date' | 'formula';
  width?: string;
  formula?: string;
  editable?: boolean;
  sortable?: boolean;
  required?: boolean;
}

interface GridData {
  rows: GridRow[];
  columns: GridColumn[];
  metadata: {
    entity: string;
    sheetName: string;
    lastModified: string;
    version: number;
    owner: string;
  };
}

interface UseSmartGridMemoryOptions {
  entity: string;
  sheetName: string;
  autoSave?: boolean;
  autoSaveDelay?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
  year?: string;
}

interface UseSmartGridMemoryReturn {
  gridData: GridData | null;
  loading: boolean;
  error: string | null;
  saveData: (data: GridData) => Promise<void>;
  loadData: () => Promise<void>;
  updateCell: (rowId: string, columnId: string, value: any) => Promise<void>;
  addRow: (row: GridRow) => Promise<void>;
  deleteRow: (rowId: string) => Promise<void>;
  logAudit: (action: string, rowId: string, columnId?: string, beforeValue?: any, afterValue?: any) => Promise<void>;
  isConnected: boolean;
}

export const useSmartGridMemory = ({
  entity,
  sheetName,
  autoSave = true,
  autoSaveDelay = 1000,
  autoRefresh = true,
  refreshInterval = 5000,
  year = new Date().getFullYear().toString()
}: UseSmartGridMemoryOptions): UseSmartGridMemoryReturn => {
  const [gridData, setGridData] = useState<GridData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [memoryProvider, setMemoryProvider] = useState<BeeMemoryProvider | null>(null);
  
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<GridData | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const subscriptionRef = useRef<any>(null);

  const memoryKey = `smartgrid:${entity}:${sheetName}`;
  const auditKey = `audit:smartgrid:${sheetName}`;
  const transactionsNamespace = `arcOS:${entity}:${year}:transactions`;
  const documentsNamespace = `arcOS:${entity}:${year}:documents`;
  const formsNamespace = `arcOS:${entity}:${year}:forms`;

  useEffect(() => {
    const initializeMemoryProvider = async () => {
      try {
        const provider = await createBeeMemoryProvider();
        if (provider) {
          setMemoryProvider(provider);
          setIsConnected(true);
          console.log('Devin: [SmartGrid] BeeMemory provider initialized');
          
          if (autoRefresh) {
            setupAutoRefresh(provider);
          }
        } else {
          console.warn('[SmartGrid] BeeMemory provider not available, using local state fallback');
          setIsConnected(false);
        }
      } catch (err) {
        console.error('[SmartGrid] Failed to initialize BeeMemory:', err);
        console.warn('[SmartGrid] Using local state fallback');
        setIsConnected(false);
      }
    };

    initializeMemoryProvider();
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let storedData = null;
      
      if (memoryProvider) {

        storedData = await memoryProvider.get(memoryKey, {});

        storedData = await (memoryProvider as any).get?.(memoryKey);
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setGridData(parsedData);
          console.log('Devin: [SmartGrid] Data loaded from BeeMemory:', memoryKey);
          setLoading(false);
          return;
        }
      } else {
        console.log('[SmartGrid] Using local state fallback mode');
      }

      if (!storedData) {
        const defaultData: GridData = {
          rows: [
            {
              id: 'row-1',
              task: 'File 1120-S',
              owner: 'Dana',
              status: 'In Progress',
              due: '2025-08-10',
              cost: 5000,
              formula: '=SUM(cost)'
            },
            {
              id: 'row-2',
              task: 'Review Tax Documents',
              owner: 'Alex',
              status: 'Pending',
              due: '2025-08-15',
              cost: 3000,
              formula: '=SUM(cost)'
            },
            {
              id: 'row-3',
              task: 'Client Meeting',
              owner: 'Sarah',
              status: 'Completed',
              due: '2025-08-05',
              cost: 1500,
              formula: '=SUM(cost)'
            },
            {
              id: 'row-4',
              task: 'Audit Preparation',
              owner: 'Mike',
              status: 'In Progress',
              due: '2025-08-20',
              cost: 4000,
              formula: '=SUM(cost)'
            },
            {
              id: 'row-5',
              task: 'Financial Analysis',
              owner: 'Lisa',
              status: 'Not Started',
              due: '2025-08-25',
              cost: 2500,
              formula: '=SUM(cost)'
            }
          ],
          columns: [
            { id: 'task', header: 'Task', type: 'text', editable: true, sortable: true, required: true },
            { id: 'owner', header: 'Owner', type: 'text', editable: true, sortable: true },
            { id: 'status', header: 'Status', type: 'text', editable: true, sortable: true },
            { id: 'due', header: 'Due Date', type: 'date', editable: true, sortable: true },
            { id: 'cost', header: 'Cost', type: 'number', editable: true, sortable: true },
            { id: 'formula', header: 'Total', type: 'formula', formula: '=SUM(cost)', sortable: false }
          ],
          metadata: {
            entity,
            sheetName,
            lastModified: new Date().toISOString(),
            version: 1,
            owner: 'system'
          }
        };
        
        setGridData(defaultData);
        if (memoryProvider) {
          await saveData(defaultData);
          console.log('[SmartGrid] Default data created and saved to BeeMemory');
        } else {
          console.log('[SmartGrid] Default data created (local state only)');
        }
      }
    } catch (err) {
      setError(`Failed to load data: ${err}`);
      console.error('[SmartGrid] Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [memoryProvider, memoryKey, entity, sheetName]);

  const saveData = useCallback(async (data: GridData) => {
    try {
      const dataToSave = {
        ...data,
        metadata: {
          ...data.metadata,
          lastModified: new Date().toISOString(),
          version: data.metadata.version + 1
        }
      };

      if (memoryProvider) {

        await memoryProvider.set(memoryKey, JSON.stringify(dataToSave), undefined, {});

        await (memoryProvider as any).set?.(memoryKey, JSON.stringify(dataToSave));
        console.log('[SmartGrid] Data saved to BeeMemory:', memoryKey);
      } else {
        console.log('[SmartGrid] Data saved to local state (BeeMemory not available)');
      }
      
      setGridData(dataToSave);
    } catch (err) {
      setError(`Failed to save data: ${err}`);
      console.error('[SmartGrid] Failed to save data:', err);
      throw err;
    }
  }, [memoryProvider, memoryKey]);

  const scheduleAutoSave = useCallback((data: GridData) => {
    if (!autoSave) return;

    pendingChangesRef.current = data;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (pendingChangesRef.current) {
        try {
          await saveData(pendingChangesRef.current);
          pendingChangesRef.current = null;
        } catch (err) {
          console.error('[SmartGrid] Auto-save failed:', err);
        }
      }
    }, autoSaveDelay);
  }, [autoSave, autoSaveDelay, saveData]);

  const updateCell = useCallback(async (rowId: string, columnId: string, value: any) => {
    if (!gridData) return;

    const oldRow = gridData.rows.find(row => row.id === rowId);
    if (!oldRow) return;

    const oldValue = oldRow[columnId];
    
    const updatedRows = gridData.rows.map(row => 
      row.id === rowId ? { ...row, [columnId]: value } : row
    );

    const newGridData = {
      ...gridData,
      rows: updatedRows,
      metadata: {
        ...gridData.metadata,
        lastModified: new Date().toISOString(),
        version: gridData.metadata.version + 1
      }
    };

    setGridData(newGridData);
    scheduleAutoSave(newGridData);

    await logAudit('update', rowId, columnId, oldValue, value);
  }, [gridData, scheduleAutoSave]);

  const addRow = useCallback(async (row: GridRow) => {
    if (!gridData) return;

    const newGridData = {
      ...gridData,
      rows: [...gridData.rows, row],
      metadata: {
        ...gridData.metadata,
        lastModified: new Date().toISOString(),
        version: gridData.metadata.version + 1
      }
    };

    setGridData(newGridData);
    scheduleAutoSave(newGridData);

    await logAudit('create', row.id, undefined, null, row);
  }, [gridData, scheduleAutoSave]);

  const deleteRow = useCallback(async (rowId: string) => {
    if (!gridData) return;

    const rowToDelete = gridData.rows.find(row => row.id === rowId);
    if (!rowToDelete) return;

    const newGridData = {
      ...gridData,
      rows: gridData.rows.filter(row => row.id !== rowId),
      metadata: {
        ...gridData.metadata,
        lastModified: new Date().toISOString(),
        version: gridData.metadata.version + 1
      }
    };

    setGridData(newGridData);
    scheduleAutoSave(newGridData);

    await logAudit('delete', rowId, undefined, rowToDelete, null);
  }, [gridData, scheduleAutoSave]);

  const logAudit = useCallback(async (
    action: string,
    rowId: string,
    columnId?: string,
    beforeValue?: any,
    afterValue?: any
  ) => {
    try {
      const auditEntry = {
        id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        userId: 'current-user',
        action,
        rowId,
        columnId,
        beforeValue,
        afterValue,
        entity,
        sheetName
      };

      if (memoryProvider) {
        const auditEntryKey = `${auditKey}:${auditEntry.id}`;

        await memoryProvider.set(auditEntryKey, JSON.stringify(auditEntry), undefined, {});

        await (memoryProvider as any).set?.(auditEntryKey, JSON.stringify(auditEntry));
        console.log('[SmartGrid] Audit entry logged to BeeMemory:', auditEntryKey);
      } else {
        console.log('[SmartGrid] Audit entry logged locally:', auditEntry);
      }
    } catch (err) {
      console.error('[SmartGrid] Failed to log audit entry:', err);
    }
  }, [memoryProvider, auditKey, entity, sheetName]);

  const setupAutoRefresh = useCallback(async (provider: BeeMemoryProvider) => {
    try {
      console.log('[SmartGrid] Setting up auto-refresh for WealthOS data changes');
      
      const handleDataChange = async (message: any) => {
        console.log(`[SmartGrid] Received change notification:`, message);
        
        if (typeof message === 'string' && (
            message.includes(transactionsNamespace) || 
            message.includes(documentsNamespace) || 
            message.includes(formsNamespace))) {
          console.log('[SmartGrid] WealthOS data changed, refreshing grid...');
          await loadData();
        }
      };

      const subscriptions = [
        `${transactionsNamespace}:*`,
        `${documentsNamespace}:*`, 
        `${formsNamespace}:*`
      ];

      for (const pattern of subscriptions) {
        try {
          const subscription = await (provider as any).subscribe?.(pattern, handleDataChange);
          subscriptionRef.current = subscription;
          console.log(`[SmartGrid] Subscribed to ${pattern} for auto-refresh`);
        } catch (err) {
          console.warn(`[SmartGrid] Failed to subscribe to ${pattern}:`, err);
        }
      }

      if (refreshInterval > 0) {
        refreshIntervalRef.current = setInterval(async () => {
          try {
            console.log('[SmartGrid] Polling for data changes...');
            await loadData();
          } catch (err) {
            console.error('[SmartGrid] Polling refresh failed:', err);
          }
        }, refreshInterval);
        console.log(`[SmartGrid] Polling refresh enabled (${refreshInterval}ms interval)`);
      }
      
    } catch (err) {
      console.error('[SmartGrid] Failed to setup auto-refresh:', err);
    }
  }, [transactionsNamespace, documentsNamespace, formsNamespace, refreshInterval, loadData]);

  const checkForWealthOSData = useCallback(async () => {
    if (!memoryProvider) return;

    try {
      const namespaces = [transactionsNamespace, documentsNamespace, formsNamespace];
      
      for (const namespace of namespaces) {
        try {

          const keys = await (memoryProvider as any).get?.(`${namespace}:*`);
          if (keys) {
            console.log(`[SmartGrid] Found WealthOS data in ${namespace}, merging with grid...`);
            
            if (namespace === transactionsNamespace) {
              const transactionData = JSON.parse(keys);
              if (Array.isArray(transactionData) && transactionData.length > 0) {
                await mergeTransactionData(transactionData);
              }
            }
          }
        } catch (err) {
          console.warn(`[SmartGrid] Failed to check ${namespace}:`, err);
        }
      }
    } catch (err) {
      console.error('[SmartGrid] Failed to check for WealthOS data:', err);
    }
  }, [memoryProvider, transactionsNamespace, documentsNamespace, formsNamespace]);

  const mergeTransactionData = useCallback(async (transactions: any[]) => {
    if (!gridData) return;

    try {
      const newRows = transactions.map((transaction, index) => ({
        id: `transaction-${Date.now()}-${index}`,
        task: transaction.description || transaction.memo || 'Imported Transaction',
        owner: transaction.payee || 'System',
        status: 'Imported',
        due: transaction.date || new Date().toISOString().split('T')[0],
        cost: Math.abs(parseFloat(transaction.amount) || 0),
        formula: '=SUM(cost)',
        source: 'csv-import',
        category: transaction.category || 'General'
      }));

      const updatedGridData = {
        ...gridData,
        rows: [...gridData.rows, ...newRows],
        metadata: {
          ...gridData.metadata,
          lastModified: new Date().toISOString(),
          version: gridData.metadata.version + 1
        }
      };

      setGridData(updatedGridData);
      if (autoSave) {
        await saveData(updatedGridData);
      }

      console.log(`[SmartGrid] Merged ${newRows.length} transaction rows into grid`);
    } catch (err) {
      console.error('[SmartGrid] Failed to merge transaction data:', err);
    }
  }, [gridData, autoSave, saveData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (memoryProvider && autoRefresh) {
      checkForWealthOSData();
    }
  }, [memoryProvider, autoRefresh, checkForWealthOSData]);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        console.log('[SmartGrid] Cleared refresh interval');
      }
      
      if (subscriptionRef.current && memoryProvider) {
        try {
          console.log('[SmartGrid] Cleaning up subscriptions');
        } catch (err) {
          console.warn('[SmartGrid] Failed to cleanup subscriptions:', err);
        }
      }
    };
  }, [memoryProvider]);

  return {
    gridData,
    loading,
    error,
    saveData,
    loadData,
    updateCell,
    addRow,
    deleteRow,
    logAudit,
    isConnected
  };
};

export default useSmartGridMemory;
